import { createSignal, createMemo, type Component, For } from "solid-js";
import { PanelLayout, type SubTab, type PanelStatus } from "@/shared/panel-base";
import { DataTable, type Column } from "@/shared/data-table";
import { createToolQuery } from "@/shared/query";
import { useToolClient } from "@/shared/tool-client";
import { useConfirm } from "@/shared/confirm-dialog";
import { useWs } from "@/shared/ws";
import { registerPanel } from "@/panel-registry";
import { fuzzyFilter } from "./lib/fuzzy";

interface DnsZone {
  ZoneName?: string;
  ZoneType?: string;
  IsAutoCreated?: boolean;
  IsReverseLookupZone?: boolean;
  DynamicUpdate?: string;
  IsDsIntegrated?: boolean;
}

interface DnsRecord {
  HostName?: string;
  RecordType?: string;
  RecordData?: string;
  Timestamp?: string;
  TimeToLive?: string | number;
}

interface ListResult<T> {
  rows: T[];
}

const RECORD_TYPES = ["A", "AAAA", "CNAME", "PTR", "MX", "TXT"];

function formatCell(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? "Oui" : "Non";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    try {
      return new Date(value).toLocaleString();
    } catch {
      return value;
    }
  }
  if (Array.isArray(value)) return value.map((v) => formatCell(v)).join("; ");
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

const DnsPanel: Component = () => {
  const ws = useWs();
  const confirm = useConfirm();
  const tools = useToolClient();
  const [selectedZone, setSelectedZone] = createSignal("");
  const [actionStatus, setActionStatus] = createSignal<PanelStatus | null>(null);
  const [zoneFilter, setZoneFilter] = createSignal("");
  const [recordFilter, setRecordFilter] = createSignal("");

  const enabled = () => ws.state.nativeAgentOnline && ws.state.supportApproved;

  const zonesQuery = createToolQuery<ListResult<DnsZone>>(() => ({
    tool: "dns_zones_list",
    params: {},
    enabled: enabled(),
    refetchInterval: 0,
  }));

  const recordsQuery = createToolQuery<ListResult<DnsRecord>>(() => ({
    tool: "dns_records_list",
    params: { zoneName: selectedZone() },
    enabled: enabled() && selectedZone() !== "",
    refetchInterval: 0,
  }));

  async function addRecord(): Promise<void> {
    const zone = selectedZone();
    if (!zone) {
      setActionStatus({ text: "Selectionnez d'abord une zone.", level: "warn" });
      return;
    }
    const typeRaw = window.prompt("Type d'enregistrement (A, AAAA, CNAME, PTR, MX, TXT):", "A");
    if (!typeRaw) return;
    const type = typeRaw.trim().toUpperCase();
    if (!RECORD_TYPES.includes(type)) {
      setActionStatus({ text: "Type non supporte.", level: "warn" });
      return;
    }
    const name = window.prompt("Nom (host) de l'enregistrement:", "");
    if (!name) return;
    const data = window.prompt(`Donnee pour ${type}:`, "");
    if (data == null || data.trim() === "") return;
    const ttlStr = window.prompt("TTL (secondes, vide = defaut):", "");
    let ttlSeconds: number | null = null;
    if (ttlStr && ttlStr.trim() !== "") {
      const n = Number(ttlStr);
      if (!Number.isFinite(n) || n < 0) {
        setActionStatus({ text: "TTL invalide.", level: "warn" });
        return;
      }
      ttlSeconds = Math.floor(n);
    }
    const ok = await confirm.confirm({
      title: "Ajouter enregistrement DNS",
      body: `Ajouter ${type} ${name} -> ${data} dans la zone ${zone} ?`,
      level: "info",
      confirmLabel: "Ajouter",
    });
    if (!ok) return;
    setActionStatus({ text: "Ajout enregistrement...", level: "info" });
    try {
      const params: Record<string, unknown> = {
        zoneName: zone,
        name: name.trim(),
        type,
        data: data.trim(),
      };
      if (ttlSeconds != null) params.ttlSeconds = ttlSeconds;
      await tools.call("dns_record_add", params, { userConfirmedAt: new Date().toISOString() });
      setActionStatus({ text: "Ajout reussi.", level: "info" });
      await recordsQuery.refetch();
    } catch (err) {
      setActionStatus({
        text: `Erreur: ${err instanceof Error ? err.message : String(err)}`,
        level: "danger",
      });
    }
  }

  async function removeRecord(row: DnsRecord): Promise<void> {
    const zone = selectedZone();
    const name = row.HostName ?? "";
    const type = row.RecordType ?? "";
    const recordData = row.RecordData ?? "";
    if (!zone || !name || !type) return;
    const ok = await confirm.confirm({
      title: `Supprimer enregistrement ${name}`,
      body: `Supprimer DNS ${type} ${name} (${recordData}) dans la zone ${zone} ?`,
      level: "danger",
      confirmLabel: "Supprimer",
      entityName: name,
    });
    if (!ok) return;
    setActionStatus({ text: `Suppression ${name}...`, level: "info" });
    try {
      await tools.call(
        "dns_record_remove",
        { zoneName: zone, name, type, recordData },
        { userConfirmedAt: new Date().toISOString() },
      );
      setActionStatus({ text: `Suppression ${name} reussie.`, level: "info" });
      await recordsQuery.refetch();
    } catch (err) {
      setActionStatus({
        text: `Erreur: ${err instanceof Error ? err.message : String(err)}`,
        level: "danger",
      });
    }
  }

  const filteredZones = createMemo(() =>
    fuzzyFilter(zoneFilter(), zonesQuery.data?.rows ?? [], (z) =>
      [z.ZoneName ?? "", z.ZoneType ?? "", z.DynamicUpdate ?? ""].join(" "),
    ),
  );

  const filteredRecords = createMemo(() =>
    fuzzyFilter(recordFilter(), recordsQuery.data?.rows ?? [], (r) =>
      [r.HostName ?? "", r.RecordType ?? "", r.RecordData ?? ""].join(" "),
    ),
  );

  const zoneColumns: Column<DnsZone>[] = [
    { accessorKey: "ZoneName", header: "ZoneName" },
    { accessorKey: "ZoneType", header: "ZoneType" },
    {
      accessorKey: "IsAutoCreated",
      header: "IsAutoCreated",
      cell: (info) => formatCell(info.getValue()),
    },
    {
      accessorKey: "IsReverseLookupZone",
      header: "IsReverseLookupZone",
      cell: (info) => formatCell(info.getValue()),
    },
    { accessorKey: "DynamicUpdate", header: "DynamicUpdate" },
    {
      accessorKey: "IsDsIntegrated",
      header: "IsDsIntegrated",
      cell: (info) => formatCell(info.getValue()),
    },
  ];

  const recordColumns: Column<DnsRecord>[] = [
    { accessorKey: "HostName", header: "HostName" },
    { accessorKey: "RecordType", header: "RecordType" },
    { accessorKey: "RecordData", header: "RecordData" },
    {
      accessorKey: "Timestamp",
      header: "Timestamp",
      cell: (info) => formatCell(info.getValue()),
    },
    { accessorKey: "TimeToLive", header: "TimeToLive" },
  ];

  const subtabs: SubTab[] = [
    {
      id: "zones",
      label: "Zones",
      render: () => (
        <>
          <div class="panel-toolbar panel-toolbar--filter">
            <input
              type="text"
              class="panel-filter-input"
              placeholder="Recherche fuzzy (ex: rev local)..."
              spellcheck={false}
              value={zoneFilter()}
              onInput={(e) => setZoneFilter(e.currentTarget.value)}
            />
            <span class="panel-toolbar-label">
              {filteredZones().length} / {(zonesQuery.data?.rows ?? []).length}
            </span>
            <button type="button" class="btn btn--xs btn--info" onClick={() => zonesQuery.refetch()}>
              Rafraichir
            </button>
          </div>
          <DataTable<DnsZone> rows={filteredZones()} columns={zoneColumns} />
        </>
      ),
    },
    {
      id: "records",
      label: "Records",
      render: () => (
        <>
          <div class="panel-toolbar panel-toolbar--filter">
            <span class="panel-toolbar-label">Zone:</span>
            <select
              class="panel-filter-input"
              value={selectedZone()}
              onChange={(e) => setSelectedZone(e.currentTarget.value)}
            >
              <option value="">-- choisir une zone --</option>
              <For each={zonesQuery.data?.rows ?? []}>
                {(z) => <option value={z.ZoneName ?? ""}>{z.ZoneName ?? ""}</option>}
              </For>
            </select>
            <button type="button" class="btn btn--xs btn--info" disabled={!selectedZone()} onClick={() => addRecord()}>
              + Ajouter
            </button>
            <button
              type="button"
              class="btn btn--xs btn--info"
              disabled={!selectedZone()}
              onClick={() => recordsQuery.refetch()}
            >
              Rafraichir
            </button>
            <input
              type="text"
              class="panel-filter-input"
              placeholder="Recherche fuzzy (ex: srv db)..."
              spellcheck={false}
              value={recordFilter()}
              onInput={(e) => setRecordFilter(e.currentTarget.value)}
            />
            <span class="panel-toolbar-label">
              {filteredRecords().length} / {(recordsQuery.data?.rows ?? []).length}
            </span>
          </div>
          <DataTable<DnsRecord>
            rows={filteredRecords()}
            columns={recordColumns}
            rowActions={(r) => (
              <button type="button" class="btn btn--xs btn--danger" onClick={() => removeRecord(r)}>
                Supprimer
              </button>
            )}
          />
        </>
      ),
    },
  ];

  const status = (): PanelStatus | undefined => {
    if (actionStatus()) return actionStatus() ?? undefined;
    if (!ws.state.nativeAgentOnline) return { text: "Module natif requis.", level: "warn" };
    if (!ws.state.supportApproved) return { text: "Validation client requise.", level: "warn" };
    if (zonesQuery.isFetching || recordsQuery.isFetching) return { text: "Chargement...", level: "info" };
    if (zonesQuery.error)
      return {
        text: `Erreur: ${zonesQuery.error instanceof Error ? zonesQuery.error.message : String(zonesQuery.error)}`,
        level: "danger",
      };
    return undefined;
  };

  return <PanelLayout title="DNS Server" subtabs={subtabs} status={status()} />;
};

registerPanel("dns", DnsPanel);
export default DnsPanel;
